import { useState, useEffect, useRef, useCallback } from "react";
import { ChevronLeft, ChevronRight, Calendar, Flame, CreditCard as CreditIcon } from "lucide-react";
import { fetchWithTimeout } from "@/utils/fetchWithTimeout";
import { formatDate } from "@/lib/dateUtils";

interface DayActivity {
  hasFuelSale: boolean;
  hasCreditSale: boolean;
}

interface ActivityCalendarProps {
  value: string; // YYYY-MM-DD
  onChange: (date: string) => void;
}

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];
const DAY_NAMES = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

function formatDisplay(dateStr: string): string {
  if (!dateStr) return "Choose Date";
  const d = new Date(dateStr + "T00:00:00");
  return formatDate(d);
}

export function ActivityCalendar({ value, onChange }: ActivityCalendarProps) {
  const today = new Date().toISOString().split("T")[0];
  const initDate = value || today;
  const initYear = parseInt(initDate.split("-")[0]);
  const initMonth = parseInt(initDate.split("-")[1]) - 1;

  const [isOpen, setIsOpen] = useState(false);
  const [viewYear, setViewYear] = useState(initYear);
  const [viewMonth, setViewMonth] = useState(initMonth);
  const [activityMap, setActivityMap] = useState<Map<string, DayActivity>>(new Map());
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handle = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [isOpen]);

  // Fetch activity for the viewed month
  const fetchMonthActivity = useCallback(async (year: number, month: number) => {
    setLoading(true);
    try {
      const pad = (n: number) => String(n).padStart(2, "0");
      const firstDay = `${year}-${pad(month + 1)}-01`;
      const lastDayNum = new Date(year, month + 1, 0).getDate();
      const lastDay = `${year}-${pad(month + 1)}-${pad(lastDayNum)}`;

      const [saleRes, creditRes] = await Promise.allSettled([
        fetchWithTimeout(
          `/api/sale-entries?from=${firstDay}&to=${lastDay}`,
          { credentials: "include" },
          8000
        ),
        fetchWithTimeout(
          `/api/credit-sales?from_date=${firstDay}&to_date=${lastDay}`,
          { credentials: "include" },
          8000
        ),
      ]);

      const map = new Map<string, DayActivity>();

      const getOrCreate = (d: string): DayActivity =>
        map.get(d) || { hasFuelSale: false, hasCreditSale: false };

      if (saleRes.status === "fulfilled" && saleRes.value.ok) {
        const j = await saleRes.value.json();
        const rows = j?.ok || j?.success ? j.rows || [] : [];
        rows.forEach((r: any) => {
          const d = (r.sale_date || "").slice(0, 10);
          if (!d || d < firstDay || d > lastDay) return;
          const ex = getOrCreate(d);
          map.set(d, { ...ex, hasFuelSale: true });
        });
      }

      if (creditRes.status === "fulfilled" && creditRes.value.ok) {
        const j = await creditRes.value.json();
        const rows = j?.ok ? j.rows || [] : [];
        rows.forEach((r: any) => {
          const raw = r.sale_date || r.saleDate || "";
          const d = raw.slice(0, 10);
          if (!d || d < firstDay || d > lastDay) return;
          const ex = getOrCreate(d);
          map.set(d, { ...ex, hasCreditSale: true });
        });
      }

      setActivityMap(map);
    } catch (err) {
      console.error("ActivityCalendar: failed to fetch activity", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) fetchMonthActivity(viewYear, viewMonth);
  }, [isOpen, viewYear, viewMonth, fetchMonthActivity]);

  const prevMonth = () => {
    if (viewMonth === 0) {
      setViewYear((y) => y - 1);
      setViewMonth(11);
    } else {
      setViewMonth((m) => m - 1);
    }
  };

  const nextMonth = () => {
    const now = new Date();
    if (viewYear === now.getFullYear() && viewMonth === now.getMonth()) return;
    if (viewMonth === 11) {
      setViewYear((y) => y + 1);
      setViewMonth(0);
    } else {
      setViewMonth((m) => m + 1);
    }
  };

  const handleDayClick = (day: number) => {
    const pad = (n: number) => String(n).padStart(2, "0");
    const dateStr = `${viewYear}-${pad(viewMonth + 1)}-${pad(day)}`;
    if (dateStr > today) return;
    onChange(dateStr);
    setIsOpen(false);
  };

  // Build calendar grid
  const firstOfMonth = new Date(viewYear, viewMonth, 1);
  const startDay = firstOfMonth.getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const cells: (number | null)[] = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const isCurrentMonthOrFuture =
    viewYear > new Date().getFullYear() ||
    (viewYear === new Date().getFullYear() && viewMonth >= new Date().getMonth());

  return (
    <div ref={containerRef} className="relative">
      {/* Trigger button */}
      <button
        id="activity-calendar-trigger"
        onClick={() => setIsOpen((o) => !o)}
        className={`flex items-center gap-2.5 bg-white hover:bg-indigo-50 border ${
          isOpen ? "border-indigo-400 ring-2 ring-indigo-100" : "border-slate-200 hover:border-indigo-300"
        } text-slate-700 font-semibold text-sm px-4 py-2.5 rounded-xl shadow-sm transition-all duration-200 active:scale-95 min-w-[180px] group`}
      >
        <Calendar className={`h-4 w-4 shrink-0 transition-colors ${isOpen ? "text-indigo-600" : "text-indigo-400 group-hover:text-indigo-600"}`} />
        <span className="flex-1 text-left">{formatDisplay(value)}</span>
        <span className="text-slate-300 text-xs">▾</span>
      </button>

      {/* Popover */}
      {isOpen && (
        <div
          ref={popoverRef}
          className="absolute top-full left-0 mt-2 z-50 bg-white border border-slate-200/80 rounded-2xl shadow-2xl shadow-slate-300/40 p-4 w-[310px]"
          style={{ animation: "calendarFadeIn 0.15s ease-out" }}
        >
          <style>{`
            @keyframes calendarFadeIn {
              from { opacity: 0; transform: translateY(-6px) scale(0.98); }
              to   { opacity: 1; transform: translateY(0) scale(1); }
            }
          `}</style>

          {/* Month navigation */}
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={prevMonth}
              className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors active:scale-90"
            >
              <ChevronLeft className="h-4 w-4 text-slate-500" />
            </button>

            <div className="text-center">
              <span className="text-sm font-extrabold text-slate-800 tracking-tight">
                {MONTH_NAMES[viewMonth]} {viewYear}
              </span>
              {loading && (
                <span className="ml-2 text-[10px] text-indigo-400 font-bold animate-pulse">
                  ●
                </span>
              )}
            </div>

            <button
              onClick={nextMonth}
              disabled={isCurrentMonthOrFuture}
              className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors active:scale-90 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronRight className="h-4 w-4 text-slate-500" />
            </button>
          </div>

          {/* Day-of-week headers */}
          <div className="grid grid-cols-7 mb-1.5">
            {DAY_NAMES.map((d) => (
              <div
                key={d}
                className="text-center text-[11px] font-bold text-slate-400 py-1"
              >
                {d}
              </div>
            ))}
          </div>

          {/* Day cells */}
          <div className="grid grid-cols-7 gap-1">
            {cells.map((day, idx) => {
              if (!day) return <div key={`empty-${idx}`} />;

              const pad = (n: number) => String(n).padStart(2, "0");
              const dateStr = `${viewYear}-${pad(viewMonth + 1)}-${pad(day)}`;
              const isSelected = dateStr === value;
              const isToday = dateStr === today;
              const isFuture = dateStr > today;
              const activity = activityMap.get(dateStr);
              const hasFuel = !loading && !!activity?.hasFuelSale;
              const hasCredit = !loading && !!activity?.hasCreditSale;
              const hasBoth = hasFuel && hasCredit;
              const hasAny = hasFuel || hasCredit;

              return (
                <button
                  key={dateStr}
                  onClick={() => handleDayClick(day)}
                  disabled={isFuture}
                  title={
                    hasBoth
                      ? "Fuel + Credit sales"
                      : hasFuel
                      ? "Fuel sale entries"
                      : hasCredit
                      ? "Credit sales only"
                      : isToday
                      ? "Today"
                      : undefined
                  }
                  className={[
                    "relative flex flex-col items-center justify-center h-9 w-full rounded-lg text-xs font-bold transition-all duration-150",
                    // Selected state
                    isSelected
                      ? "bg-indigo-600 text-white shadow-lg shadow-indigo-200 scale-110 z-10"
                      : "",
                    // Has both types of data
                    !isSelected && hasBoth && !isFuture
                      ? "bg-emerald-500 text-white hover:bg-emerald-600"
                      : "",
                    // Fuel only
                    !isSelected && hasFuel && !hasCredit && !isFuture
                      ? "bg-emerald-50 text-emerald-800 hover:bg-emerald-100 border border-emerald-200"
                      : "",
                    // Credit only
                    !isSelected && hasCredit && !hasFuel && !isFuture
                      ? "bg-amber-50 text-amber-800 hover:bg-amber-100 border border-amber-200"
                      : "",
                    // No data, not future
                    !isSelected && !hasAny && !isFuture
                      ? "text-slate-600 hover:bg-slate-100"
                      : "",
                    // Future
                    isFuture ? "text-slate-300 cursor-not-allowed" : "cursor-pointer",
                    // Today ring
                    isToday && !isSelected ? "ring-2 ring-indigo-400 ring-offset-1" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  <span className="leading-none">{day}</span>

                  {/* Activity indicator dots below the number */}
                  {!isSelected && !isFuture && (hasFuel || hasCredit) && (
                    <div className="flex gap-0.5 mt-0.5">
                      {hasFuel && (
                        <span
                          className={`w-1 h-1 rounded-full ${
                            hasBoth ? "bg-white/70" : "bg-emerald-500"
                          }`}
                        />
                      )}
                      {hasCredit && (
                        <span
                          className={`w-1 h-1 rounded-full ${
                            hasBoth ? "bg-white/70" : "bg-amber-500"
                          }`}
                        />
                      )}
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* Legend */}
          <div className="mt-4 pt-3 border-t border-slate-100 grid grid-cols-2 gap-2">
            <div className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-sm bg-emerald-500 shrink-0" />
              <span className="text-[10px] text-slate-500 font-semibold">Fuel + Credit</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-sm bg-emerald-50 border border-emerald-300 shrink-0" />
              <span className="text-[10px] text-slate-500 font-semibold">Fuel Sale Only</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-sm bg-amber-50 border border-amber-300 shrink-0" />
              <span className="text-[10px] text-slate-500 font-semibold">Credit Sale Only</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-sm ring-2 ring-indigo-400 shrink-0" />
              <span className="text-[10px] text-slate-500 font-semibold">Today</span>
            </div>
          </div>

          {/* Quick-jump to today */}
          {value !== today && (
            <button
              onClick={() => { onChange(today); setIsOpen(false); }}
              className="mt-3 w-full text-center text-xs font-bold text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 py-1.5 rounded-lg transition-colors"
            >
              Jump to Today
            </button>
          )}
        </div>
      )}
    </div>
  );
}
